import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import { FaUser } from 'react-icons/fa'
import { HiLocationMarker } from 'react-icons/hi'

import { BeltVisual } from '@goodfellas/belt-visual'
import { Colors } from '@goodfellas/graduation'

import { normalizeBeltColor } from '../../functions/graduation'
import {
  CompetitionMatch,
  calculateCompetitionStats,
} from '../../types/competition'

interface Member {
  id: string
  name: string
  belt: string
  avatar: string | null
}

interface GymProfileData {
  id: string
  type: 'GYM'
  name: string
  avatar: string | null
  bio?: string
  location?: string
  size?: number
}

interface Props {
  profile: GymProfileData
  members: Member[]
  matches: CompetitionMatch[]
}

export default function GymProfileView({ profile, members, matches }: Props) {
  const { t } = useTranslation('common')
  const stats = calculateCompetitionStats(matches)

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-20">
      {/* Cover / Header */}
      <div className="relative h-64 md:h-80 bg-gradient-to-r from-red-900 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[url('/images/bg/gf-grad-red.jpg')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 -mt-32 relative z-10">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Sidebar / Profile Card */}
          <div className="w-full lg:w-1/3 xl:w-1/4">
            <div className="bg-gray-900 border border-white/10 rounded-3xl p-6 shadow-2xl backdrop-blur-sm">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-40 h-40 rounded-full border-4 border-red-600 p-1 bg-gray-900 shadow-xl overflow-hidden">
                    {profile.avatar ? (
                      <img
                        src={profile.avatar}
                        alt={profile.name}
                        className="w-full h-full rounded-full object-cover my-0"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center">
                        <FaUser className="text-5xl text-white/20" />
                      </div>
                    )}
                  </div>
                </div>

                <h1 className="text-3xl font-serif font-bold mb-1">
                  {profile.name}
                </h1>
                <p className="text-red-500 font-sans font-semibold mb-4">
                  {t('search.gym') || 'ACADEMIA'}
                </p>

                {profile.location && (
                  <div className="flex items-center gap-2 text-white/60 mb-6">
                    <HiLocationMarker />
                    <span>{profile.location}</span>
                  </div>
                )}

                <div className="w-full pt-6 border-t border-white/5 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {profile.size || members.length}
                      </div>
                      <div className="text-xs text-white/40 uppercase">
                        {t('search.members') || 'MEMBROS'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {stats.totalMatches}
                      </div>
                      <div className="text-xs text-white/40 uppercase">
                        {t('search.matches') || 'LUTAS'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{stats.wins}</div>
                      <div className="text-xs text-white/40 uppercase">
                        {t('search.wins') || 'VITÓRIAS'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{stats.losses}</div>
                      <div className="text-xs text-white/40 uppercase">
                        {t('search.losses') || 'DERROTAS'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-8 w-full">
            {/* Members Grid */}
            <div>
              <h2 className="text-2xl font-serif font-bold mb-6">
                {t('search.members') || 'MEMBROS'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {members.map((member) => (
                  <Link
                    key={member.id}
                    href={`/fighters/${member.id}`}
                    className="group bg-gray-900 border border-white/10 rounded-2xl p-4 flex items-center gap-4 hover:border-red-500/50 hover:bg-gray-800 transition-all cursor-pointer h-full"
                  >
                    {/* Avatar Container with Integrated Belt */}
                    <div className="relative flex-shrink-0 mb-1">
                      <div className="w-16 h-16 rounded-full border-2 border-red-600 p-0.5 overflow-hidden bg-black shadow-lg relative z-0">
                        {member.avatar ? (
                          <img
                            src={member.avatar}
                            alt={member.name}
                            className="w-full h-full rounded-full object-cover my-0"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-full">
                            <FaUser className="text-xl text-white/20" />
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-[17px] left-1/2 -translate-x-1/2 z-10 w-max">
                        <div className="scale-[0.65] origin-top shadow-sm">
                          <BeltVisual
                            color={
                              normalizeBeltColor(member.belt) || Colors.White
                            }
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right Container: Info */}
                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-1 pl-1">
                      <h3 className="font-bold text-white group-hover:text-red-500 transition-colors line-clamp-2 text-lg leading-tight">
                        {member.name}
                      </h3>
                      <span className="text-xs text-white/40 uppercase tracking-wider group-hover:text-white/60">
                        {t('search.see_stats') || 'Ver estatísticas'} &rarr;
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
